import { Add } from "@mui/icons-material";
import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useContext, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import { normalizeInputValue } from "../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import SsnField from "../../../common/components/functional/ssnField/SsnField";
import { TextFieldLight } from "../../../common/components/styled/styled";
import checkIsSsnTemporary from "../../../common/helpers/checkIsTemporarySsn";
import filterFalsyParameters from "../../../common/helpers/filterFalsyParameters";
import {
  fromIsoDateString,
  isoFormat,
  leadingZerosDateFormat,
  shortDateFormat,
  today,
  toIsoDateString,
} from "../../../common/helpers/formatDateTime";
import isEmpty from "../../../common/helpers/isEmpty";
import { Optional } from "../../../common/types/nullable";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import {
  Gender,
  Talent,
  TalentSalaryInformation,
  TalentTaxDeductionCard,
  TalentTaxInformation,
  talentIsDeactivated,
} from "../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentSalaryInformation,
  updateTalentTaxDeductionCard,
  updateTalentTaxInformation,
} from "../../talents/talentsSlice";
import {
  UpdateTalentSalaryInformation,
  UpdateTalentTaxInformation,
} from "../../talents/types/commands";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { canExpertEditTalentSalaryInfo } from "../../user/accessRightChecks";
import { RemovePaymentInformationButton } from "./RemovePaymentInformationButton";

const EXPERT_MANUAL = "EXPERT_MANUAL";
const MYTAX = "MYTAX";

type TaxDeductionCardFormValues = {
  updatedBy: string;
  updatedAt: string;
  validFrom: Date | null;
  withholdingRate: string;
  incomeCeiling: string;
  additionalWithholdingRate: string;
};

type FormValues = {
  salaryInformation: {
    ssn: string;
    iban: string;
    bic: string;
    gender: Gender;
  };
  taxInformation: {
    taxNonresidentAddress?: string;
    taxNumber: string;
    insurancePaymentOutsideA1: boolean;
  };
  taxDeductionCard: TaxDeductionCardFormValues;
};

const replaceCommaWithDot = (value?: string) =>
  !!value ? value.replace(",", ".") : "";
const replaceDotWithComma = (value?: string) =>
  !!value ? value.replace(".", ",") : "";

interface Props {
  fontSize?: string;
}

const PaymentAndTaxInformation = ({ fontSize }: Props) => {
  const [editSalaryAndTaxInfo, setEditSalaryAndTaxInfo] = useState(false);
  const [editTaxDeductionCard, setEditTaxDeductionCard] = useState(false);
  const [editWithholdingRate, setEditWithholdingRate] = useState(false);
  const [editTaxNumber, setEditTaxNumber] = useState(false);
  const [isTemporarySsn, setIsTemporarySsn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const { canEditTalentSalaryInfo, canEditTalentTaxNumber } =
    useContext(AccessRightContext);

  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const talentSalaryInformation: Optional<TalentSalaryInformation> =
    talent.salaryInformation;

  const talentTaxInformation: Optional<TalentTaxInformation> =
    talent.taxInformation;
  const talentTaxDeductionCard: Optional<TalentTaxDeductionCard> =
    talent.taxDeductionCard;

  const dispatch = useAppDispatch();

  const defaultValues: FormValues = useMemo(
    () => ({
      salaryInformation: {
        ssn: talentSalaryInformation?.ssn || "",
        iban: talentSalaryInformation?.iban || "",
        bic: talentSalaryInformation?.bic || "",
        gender: talentSalaryInformation?.gender || Gender.Empty,
      },
      taxInformation: {
        taxNonresidentAddress:
          talentTaxInformation?.taxNonresidentAddress || "",
        taxNumber: talentTaxInformation?.taxNumber || "",
        insurancePaymentOutsideA1:
          talentTaxInformation?.insurancePaymentOutsideA1 || false,
      },
      taxDeductionCard: {
        updatedBy: talentTaxDeductionCard?.taxDeductionCard.updatedBy || "",
        updatedAt: fromIsoDateString(
          talentTaxDeductionCard?.taxDeductionCard.updatedAt,
          shortDateFormat,
        ),
        validFrom: talentTaxDeductionCard?.taxDeductionCard.validFrom
          ? new Date(talentTaxDeductionCard?.taxDeductionCard.validFrom)
          : null,
        withholdingRate: replaceDotWithComma(
          talentTaxDeductionCard?.taxDeductionCard.withholdingRate,
        ),
        incomeCeiling: replaceDotWithComma(
          talentTaxDeductionCard?.taxDeductionCard.incomeCeiling,
        ),
        additionalWithholdingRate: replaceDotWithComma(
          talentTaxDeductionCard?.taxDeductionCard.additionalWithholdingRate,
        ),
      },
    }),
    [talentSalaryInformation, talentTaxInformation, talentTaxDeductionCard],
  );

  const methods = useForm({ mode: "onChange", defaultValues });
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { errors, isDirty, dirtyFields },
    watch,
    setError,
    clearErrors,
  } = methods;

  const ssn = watch("salaryInformation.ssn");

  useEffect(() => {
    // reset async default values after values are fetched
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [reset, defaultValues]);

  useEffect(() => {
    if (
      editSalaryAndTaxInfo &&
      !errors.salaryInformation?.ssn &&
      ssn.length === 11
    ) {
      const genderNumbers = ssn.substring(7, 10);
      const tempSSN = checkIsSsnTemporary(ssn);
      let gender = getValues("salaryInformation.gender");

      if (tempSSN) {
        setIsTemporarySsn(true);
        if (!gender || gender === Gender.Empty) {
          setError("salaryInformation.gender", { message: "required" });
        }
      } else if (parseInt(genderNumbers) % 2 === 0) {
        clearErrors("salaryInformation.gender");
        setIsTemporarySsn(false);
        gender = Gender.Female;
      } else {
        clearErrors("salaryInformation.gender");
        setIsTemporarySsn(false);
        gender = Gender.Male;
      }
      setValue("salaryInformation.gender", gender);
      return;
    }
  }, [
    ssn,
    dirtyFields.salaryInformation?.ssn,
    errors.salaryInformation?.ssn,
    setValue,
    setError,
    clearErrors,
    getValues,
    editSalaryAndTaxInfo,
  ]);

  const paymentInformationDirty = () => {
    const salaryInformation = dirtyFields.salaryInformation;

    if (!salaryInformation) return false;

    const fields = [
      dirtyFields.salaryInformation.ssn,
      dirtyFields.salaryInformation.iban,
      dirtyFields.salaryInformation.bic,
      dirtyFields.salaryInformation.gender,
    ];

    return fields.some((field) => field);
  };

  const taxInformationDirty = () => {
    const taxInformation = dirtyFields.taxInformation;

    if (!taxInformation) return false;

    const fields = [
      dirtyFields.taxInformation.taxNonresidentAddress,
      dirtyFields.taxInformation.taxNumber,
      dirtyFields.taxInformation.insurancePaymentOutsideA1,
    ];

    return fields.some((field) => field);
  };

  const taxDeductionCardDirty = () => {
    const taxDeductionCard = dirtyFields.taxDeductionCard;

    if (!taxDeductionCard) return false;

    const fields = [
      dirtyFields.taxDeductionCard.updatedBy,
      dirtyFields.taxDeductionCard.updatedAt,
      dirtyFields.taxDeductionCard.validFrom,
      dirtyFields.taxDeductionCard.withholdingRate,
      dirtyFields.taxDeductionCard.incomeCeiling,
      dirtyFields.taxDeductionCard.additionalWithholdingRate,
    ];

    return fields.some((field) => field);
  };

  const ssnDirtyOnly = (): boolean => {
    const isSsnDirtyOnly =
      (dirtyFields.salaryInformation?.ssn ||
        dirtyFields.salaryInformation?.gender) &&
      !dirtyFields.salaryInformation?.bic &&
      !dirtyFields.salaryInformation?.iban &&
      !taxInformationDirty() &&
      !taxDeductionCardDirty();

    return isSsnDirtyOnly || false;
  };

  const ibanAndBicDirty = (): boolean => {
    const isIbanBicDirtyOnly =
      dirtyFields.salaryInformation?.bic &&
      dirtyFields.salaryInformation?.iban &&
      !taxInformationDirty() &&
      !taxDeductionCardDirty();

    return isIbanBicDirtyOnly || false;
  };

  const canSavePaymentInfoOnly = () => {
    // ssn/gender can be saved separately
    if (ssnDirtyOnly()) return true;

    if (ibanAndBicDirty()) {
      const values = getValues("salaryInformation");
      // iban and bic must be saved together
      return values.iban && values.bic;
    }
    return false;
  };

  const onEditTaxDeductionCard = () => {
    if (!editTaxDeductionCard) {
      setValue("taxDeductionCard.updatedAt", today(shortDateFormat), {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false,
      });
      setValue("taxDeductionCard.updatedBy", EXPERT_MANUAL, {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false,
      });
      setEditTaxDeductionCard(true);
    }
  };

  const onEdit = () => {
    if (!editSalaryAndTaxInfo) {
      if (canEditTalentSalaryInfo) {
        setEditSalaryAndTaxInfo(true);
      } else if (canEditTalentTaxNumber) {
        setEditTaxNumber(true);
      }
    }

    if (
      editSalaryAndTaxInfo ||
      editWithholdingRate ||
      editTaxDeductionCard ||
      editTaxNumber
    ) {
      handleSubmit(onSubmit, onError)();
    }
  };
  const onError = (errors: any, e: any) => {
    console.error("PaymentAndTaxInformation submit failed", errors, e);
  };

  const onCancel = () => {
    reset(defaultValues);
    cancelEdits();
  };

  const cancelEdits = () => {
    setEditSalaryAndTaxInfo(false);
    setEditTaxDeductionCard(false);
    setEditWithholdingRate(false);
    setEditTaxNumber(false);
  };

  const taxDeductionCardDataNotEmpty = (
    taxDeductionCard: TaxDeductionCardFormValues,
  ): boolean => {
    return (
      taxDeductionCard.validFrom !== null &&
      taxDeductionCard.withholdingRate !== "" &&
      taxDeductionCard.incomeCeiling !== "" &&
      taxDeductionCard.additionalWithholdingRate !== ""
    );
  };

  const onSubmit = (data: FormValues) => {
    const shouldUpdateSalaryInformation = paymentInformationDirty();
    const shouldUpdateTaxInformation = taxInformationDirty();
    const shouldUpdateTaxDeductionCard =
      taxDeductionCardDirty() &&
      taxDeductionCardDataNotEmpty(data.taxDeductionCard);

    const salaryInformation = data.salaryInformation;
    const taxInformation = data.taxInformation;
    const taxDeductionCard = data.taxDeductionCard;

    const salaryInfoCommandPayload: UpdateTalentSalaryInformation = {
      talentId: talent.id,
      version: talentSalaryInformation?.version || 0,
      ssn: salaryInformation.ssn,
      iban: salaryInformation.iban,
      bic: salaryInformation.bic,
      gender: salaryInformation.gender,
    };

    const taxInfoCommandPayload: UpdateTalentTaxInformation = {
      talentId: talent.id,
      version: talentTaxInformation?.version || 0,
      taxNonresidentAddress: taxInformation.taxNonresidentAddress,
      taxNumber: taxInformation.taxNumber,
      insurancePaymentOutsideA1: taxInformation.insurancePaymentOutsideA1,
    };

    const filterSalaryInfoCommandPayload = () => {
      const { talentId, version, ...rest } = salaryInfoCommandPayload;

      const filteredFields = filterFalsyParameters(rest);

      return {
        talentId,
        version,
        ...filteredFields,
      };
    };

    const dispatchSalaryInfoUpdate = () =>
      shouldUpdateSalaryInformation
        ? dispatch(
            updateTalentSalaryInformation(filterSalaryInfoCommandPayload()),
          )
            .then((action) => isFulfilled(action))
            .catch((e) => {
              console.error("updateTalentSalaryInformation failed", e);
              return false;
            })
        : Promise.resolve(true);

    const dispatchTaxInformationUpdate = () =>
      shouldUpdateTaxInformation
        ? dispatch(updateTalentTaxInformation(taxInfoCommandPayload)).then(
            (action) => isFulfilled(action),
          )
        : Promise.resolve(true);

    const dispatchTaxDeductionCardUpdate = () =>
      shouldUpdateTaxDeductionCard
        ? dispatch(
            updateTalentTaxDeductionCard({
              talentId: talent.id,
              version: talentTaxDeductionCard?.version || 0,
              taxDeductionCard: {
                updatedBy: taxDeductionCard.updatedBy,
                updatedAt: toIsoDateString(
                  taxDeductionCard.updatedAt,
                  shortDateFormat,
                ),
                validFrom: taxDeductionCard.validFrom
                  ? format(taxDeductionCard.validFrom, isoFormat)
                  : "",
                withholdingRate: Number.parseFloat(
                  replaceCommaWithDot(taxDeductionCard.withholdingRate),
                ).toString(),
                incomeCeiling: replaceCommaWithDot(
                  taxDeductionCard.incomeCeiling,
                ),
                additionalWithholdingRate: Number.parseFloat(
                  replaceCommaWithDot(
                    taxDeductionCard.additionalWithholdingRate,
                  ),
                ).toString(),
              },
            }),
          ).then((action) => isFulfilled(action))
        : Promise.resolve(true);

    Promise.allSettled([
      dispatchSalaryInfoUpdate(),
      dispatchTaxInformationUpdate(),
      dispatchTaxDeductionCardUpdate(),
    ]).then((results) => {
      let failed: number[] = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          if (!result.value) {
            failed.push(index);
          }
        } else {
          failed.push(index);
        }
      });
      if (failed.length === 0) {
        //console.log("all good");
        completeSubmit();
      } else {
        //console.log("failed", failed);
        if (failed.includes(0)) {
          //console.error("failed salary info update");
        } else {
          if (failed.includes(1)) {
            //console.error("failed tax info update");
          } else {
            // 0 and 1 good, editing can be set to false
            setEditSalaryAndTaxInfo(false);
          }
        }

        if (failed.includes(2)) {
          console.error("failed tax deduction card update");
        } else {
          setEditTaxDeductionCard(false);
          setEditWithholdingRate(false);
        }
      }
    });
  };

  const completeSubmit = () => {
    setIsLoading(false);
    reset(getValues());
    cancelEdits();
  };

  const validatePercentage = (value: string) => {
    if (!value) return true; // allow empty value
    const normalizedValue = normalizeInputValue(value);
    const numberValue = Number.parseFloat(normalizedValue);
    return (
      !isNaN(numberValue) && numberValue >= 0 && normalizedValue.length <= 5
    );
  };

  const canUpdateWithholdingPercentage = (withholdingPercentage: string) => {
    if (!!withholdingPercentage) return true;

    const normalizedNewValue = normalizeInputValue(withholdingPercentage);
    const normalizedDefaultValue = normalizeInputValue(
      defaultValues.taxDeductionCard.withholdingRate,
    );
    return normalizedNewValue > normalizedDefaultValue;
  };

  const sourceTranslation = (source: string) => {
    switch (source) {
      case MYTAX:
        return t("taxDeductionCard:updatedBy_mytax");
      case EXPERT_MANUAL:
        return t("taxDeductionCard:updatedBy_hand");
      default:
        return "";
    }
  };

  const formEditActive =
    editSalaryAndTaxInfo ||
    editTaxDeductionCard ||
    editWithholdingRate ||
    editTaxNumber;

  const canEdit =
    canEditTalentSalaryInfo ||
    (canEditTalentTaxNumber && !!defaultValues.salaryInformation.ssn);

  const UpdatedBy = () => {
    const marginRight = "10px";

    const whenEditing = (
      <Typography sx={{ marginRight }}>
        {t("taxDeductionCard:updatedBy_hand") + " " + today(shortDateFormat)}
      </Typography>
    );

    const whenNotEditing = defaultValues.taxDeductionCard.updatedBy ? (
      <Typography sx={{ marginRight }}>
        {sourceTranslation(defaultValues.taxDeductionCard.updatedBy) +
          " " +
          defaultValues.taxDeductionCard?.updatedAt}
      </Typography>
    ) : (
      <></>
    );

    return editTaxDeductionCard ? whenEditing : whenNotEditing;
  };

  const saveDisabled = !isEmpty(errors) || !isDirty || isLoading;
  const allowGenderEdit = formEditActive && isTemporarySsn;

  const defaultWithholdingRate = defaultValues.taxDeductionCard.withholdingRate;
  const withHoldingPercentageDisplayValue = defaultWithholdingRate
    ? `${defaultWithholdingRate} %`
    : "";

  const defaultAdditionalWithholdingRate =
    defaultValues.taxDeductionCard.additionalWithholdingRate;
  const additionalWithHoldingPercentageDisplayValue =
    defaultAdditionalWithholdingRate
      ? `${defaultAdditionalWithholdingRate} %`
      : "";

  /**
   * Checks if given iban (without spaces) is valid one. This piece of code stolen from backend IBAN.kt
   */
  const isValidIBAN = (normalizedIBAN: string): boolean => {
    // Rearrange the IBAN
    if (/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(normalizedIBAN)) {
      const rearrangedIBAN =
        normalizedIBAN.slice(4) + normalizedIBAN.slice(0, 4);

      // Replace letters with corresponding digits
      const digitIBAN = rearrangedIBAN
        .split("")
        .map((char) => {
          if (/\d/.test(char)) return char;
          return (char.charCodeAt(0) - "A".charCodeAt(0) + 10).toString();
        })
        .join("");

      // Check if the IBAN is valid
      const bigIntMod = BigInt(digitIBAN) % BigInt(97);
      return bigIntMod === BigInt(1);
    }
    return false;
  };

  return (
    <Box sx={{ fontSize: fontSize ? fontSize : "1rem" }}>
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <FormSection
            title={t("salaryInformation:salaryInformation")}
            margin
            noBorder
            edit={formEditActive}
            onEdit={onEdit}
            onCancel={onCancel}
            saveDisabled={saveDisabled}
            readOnly={!canEdit || talentIsDeactivated(talent)}
          >
            <OverlaySpinner isLoading={isLoading} />
            {/*ssn*/}
            <Grid
              item
              container
              xs={3}
              mt={editSalaryAndTaxInfo ? "7px" : "0px"}
              justifyContent="flex-end"
            >
              <Typography textAlign="end" variant="secondary">
                {t("salaryInformation:ssn")}
              </Typography>
            </Grid>
            <Grid item container alignItems="center" xs={9} gap={1}>
              <SsnField
                name="salaryInformation.ssn"
                edit={editSalaryAndTaxInfo}
                error={errors.salaryInformation?.ssn}
                helperText={
                  t(
                    `common:${errors.salaryInformation?.ssn?.message}`,
                  ) as string
                }
              />
              {talentSalaryInformation?.ssn && (
                <RemovePaymentInformationButton
                  talentId={talent.id}
                  version={talentSalaryInformation.version}
                  field="ssn"
                  disabled={
                    formEditActive || isLoading || talentIsDeactivated(talent)
                  }
                />
              )}
            </Grid>
            {/* gender */}
            <>
              <Grid
                item
                container
                xs={3}
                justifyContent="flex-end"
                alignItems="center"
              >
                <Typography textAlign="end" variant="secondary">
                  {t("gender")}
                </Typography>
              </Grid>
              <Grid item container alignItems="center" xs={9} gap={1}>
                <Controller
                  name="salaryInformation.gender"
                  control={control}
                  rules={{
                    required: {
                      value: editSalaryAndTaxInfo,
                      message: "gender required",
                    },
                  }}
                  render={({ field }) =>
                    allowGenderEdit ? (
                      <TextField
                        {...field}
                        select
                        size="small"
                        fullWidth
                        sx={{ maxWidth: "136px" }}
                        error={!!errors.salaryInformation?.gender}
                      >
                        <MenuItem value={Gender.Female}>
                          {t(`common:female`)}
                        </MenuItem>
                        <MenuItem value={Gender.Male}>
                          {t(`common:male`)}
                        </MenuItem>
                      </TextField>
                    ) : (
                      <Grid item container xs={5}>
                        <Typography>
                          {field.value
                            ? t(`common:${field.value.toLowerCase()}`)
                            : t("common:notselected")}
                        </Typography>
                        <InfoTooltip
                          title={t("genderInfo")}
                          style={{
                            marginLeft: "8px",
                            marginTop: "5px",
                          }}
                        />
                      </Grid>
                    )
                  }
                />
              </Grid>
            </>
            {/*iban*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("salaryInformation:iban")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="salaryInformation.iban"
                control={control}
                rules={{
                  required: {
                    value: canSavePaymentInfoOnly()
                      ? false
                      : editSalaryAndTaxInfo,
                    message: "need iban",
                  },
                  validate: (value) => {
                    if (!value || value === talentSalaryInformation?.iban)
                      return true;
                    const normalizedIBAN = value.replace(/\s+/g, "");
                    return isValidIBAN(normalizedIBAN);
                  },
                }}
                render={({ field: { name, value, onChange } }) =>
                  editSalaryAndTaxInfo ? (
                    <Grid item container xs={5}>
                      <TextField
                        fullWidth
                        name={name}
                        value={value}
                        onChange={onChange}
                        error={!!errors.salaryInformation?.iban}
                        size="small"
                      />
                    </Grid>
                  ) : (
                    <Typography>
                      {defaultValues.salaryInformation.iban}
                    </Typography>
                  )
                }
              />
              {(talentSalaryInformation?.iban ||
                talentSalaryInformation?.bic) && (
                <RemovePaymentInformationButton
                  talentId={talent.id}
                  version={talentSalaryInformation.version}
                  field="bankInfo"
                  disabled={
                    formEditActive || isLoading || talentIsDeactivated(talent)
                  }
                />
              )}
            </Grid>
            {/*bic*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("salaryInformation:bic")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="salaryInformation.bic"
                control={control}
                rules={{
                  required: {
                    value: canSavePaymentInfoOnly()
                      ? false
                      : editSalaryAndTaxInfo,
                    message: "",
                  },
                }}
                render={({ field: { name, value, onChange } }) =>
                  editSalaryAndTaxInfo ? (
                    <Grid item container xs={3}>
                      <TextField
                        fullWidth
                        name={name}
                        value={value}
                        onChange={onChange}
                        error={!!errors.salaryInformation?.bic}
                        size="small"
                      />
                    </Grid>
                  ) : (
                    <Typography>
                      {defaultValues.salaryInformation.bic}
                    </Typography>
                  )
                }
              />
            </Grid>
            {/*taxNonresidentAddress*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("taxInformation:taxNonresidentAddress")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="taxInformation.taxNonresidentAddress"
                control={control}
                render={({ field: { name, value, onChange } }) =>
                  editSalaryAndTaxInfo ? (
                    <Grid item container xs={8}>
                      <TextField
                        fullWidth
                        name={name}
                        value={value}
                        onChange={onChange}
                        error={!!errors.taxInformation?.taxNonresidentAddress}
                        size="small"
                        multiline
                        rows={4}
                      />
                    </Grid>
                  ) : (
                    <Typography>
                      {defaultValues.taxInformation.taxNonresidentAddress}
                    </Typography>
                  )
                }
              />
            </Grid>
            {/*taxNumber*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("taxInformation:taxNumber")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="taxInformation.taxNumber"
                control={control}
                render={({ field: { name, value, onChange } }) =>
                  editSalaryAndTaxInfo || editTaxNumber ? (
                    <Grid item container xs={3}>
                      <TextField
                        fullWidth
                        name={name}
                        value={value}
                        onChange={onChange}
                        error={!!errors.taxInformation?.taxNumber}
                        size="small"
                      />
                    </Grid>
                  ) : (
                    <Typography>
                      {defaultValues.taxInformation.taxNumber}
                    </Typography>
                  )
                }
              />
            </Grid>
            {/*taxDeductionCard.updatedBy*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("taxDeductionCard:taxDeductionCard")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="taxDeductionCard.updatedBy"
                control={control}
                rules={
                  {
                    // required: { value: false, message: "" },
                  }
                }
                render={() => (
                  <Grid container alignItems="center">
                    <UpdatedBy />
                    {!editTaxDeductionCard && (
                      <CustomButton
                        accessRightChecks={[canExpertEditTalentSalaryInfo]}
                        disabled={talentIsDeactivated(talent)}
                        onClick={() => onEditTaxDeductionCard()}
                        sx={{
                          height: "30px",
                          textTransform: "none",
                          borderRadius: "15px",
                          fontWeight: "700",
                        }}
                        variant="outlined"
                        startIcon={<Add />}
                      >
                        {t("taxDeductionCard:taxDeductionCard")}
                      </CustomButton>
                    )}
                  </Grid>
                )}
              />
            </Grid>
            {/*taxDeductionCard.validFrom*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("taxDeductionCard:validFrom")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="taxDeductionCard.validFrom"
                control={control}
                rules={{
                  required: {
                    value: canSavePaymentInfoOnly()
                      ? false
                      : editWithholdingRate || editTaxDeductionCard,
                    message: t("common:requiredField"),
                  },
                  validate: (value) =>
                    value ? value.getTime() <= new Date().getTime() : true,
                }}
                render={({ field: { value, onChange } }) =>
                  editTaxDeductionCard ? (
                    <DesktopDatePicker
                      value={value}
                      format="dd.MM.yyyy"
                      onChange={(newDate: Date | null) => onChange(newDate)}
                      slots={{ textField: TextFieldLight }}
                      maxDate={new Date()}
                      slotProps={{
                        textField: {
                          sx: { width: "180px" },
                          error: !!errors.taxDeductionCard?.validFrom,
                          size: "small",
                        },
                      }}
                    />
                  ) : (
                    <Typography>
                      {defaultValues.taxDeductionCard.validFrom &&
                        format(
                          defaultValues.taxDeductionCard?.validFrom,
                          leadingZerosDateFormat,
                        )}
                    </Typography>
                  )
                }
              />
            </Grid>
            {/*taxDeductionCard.withholdingRate*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("taxDeductionCard:withholdingRate")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="taxDeductionCard.withholdingRate"
                control={control}
                rules={{
                  required: {
                    value: canSavePaymentInfoOnly()
                      ? false
                      : editTaxDeductionCard || editWithholdingRate,
                    message: "",
                  },
                  validate: (value) => {
                    if (canSavePaymentInfoOnly()) return true;
                    // allow empty value
                    if (
                      !value &&
                      !(editTaxDeductionCard || editWithholdingRate)
                    ) {
                      return true;
                    }
                    return (
                      validatePercentage(value) &&
                      canUpdateWithholdingPercentage(value)
                    );
                  },
                }}
                render={({ field: { name, value, onChange } }) =>
                  editTaxDeductionCard || editWithholdingRate ? (
                    <NumericFormat
                      name={name}
                      value={value}
                      decimalSeparator=","
                      valueIsNumericString
                      customInput={TextField}
                      onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                      }}
                      error={!!errors.taxDeductionCard?.withholdingRate}
                      size="small"
                    />
                  ) : (
                    <Grid container alignItems="center">
                      <Typography>
                        {withHoldingPercentageDisplayValue}
                      </Typography>
                      {talentTaxDeductionCard ? (
                        <CustomButton
                          accessRightChecks={[canExpertEditTalentSalaryInfo]}
                          onClick={() => setEditWithholdingRate(true)}
                          sx={{
                            height: "30px",
                            textTransform: "none",
                            borderRadius: "15px",
                            fontWeight: "700",
                            marginLeft: defaultWithholdingRate ? "10px" : "0px",
                          }}
                          variant="outlined"
                          startIcon={<Add />}
                        >
                          {t("taxDeductionCard:raiseWithholdingRate")}
                        </CustomButton>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  )
                }
              />
            </Grid>
            {/*taxDeductionCard.incomeCeiling*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("taxDeductionCard:incomeCeiling")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="taxDeductionCard.incomeCeiling"
                control={control}
                rules={{
                  required: {
                    value: canSavePaymentInfoOnly()
                      ? false
                      : editTaxDeductionCard,
                    message: "",
                  },
                  validate: (value) =>
                    !value || Boolean(Number.parseFloat(value)),
                }}
                render={({ field: { name, value, onChange } }) =>
                  editTaxDeductionCard ? (
                    <TextField
                      name={name}
                      value={value}
                      onChange={(e) => {
                        const value = e.target.value;
                        const isFloat = !!Number.parseFloat(value);
                        if (isFloat || value === "") onChange(value);
                      }}
                      error={!!errors.taxDeductionCard?.incomeCeiling}
                      size="small"
                    />
                  ) : (
                    <Typography>
                      {replaceDotWithComma(
                        defaultValues.taxDeductionCard.incomeCeiling,
                      )}
                    </Typography>
                  )
                }
              />
            </Grid>
            {/*taxDeductionCard.additionalWithholdingRate*/}
            <Grid
              item
              container
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography textAlign="end" variant="secondary">
                {t("taxDeductionCard:additionalWithholdingRate")}
              </Typography>
            </Grid>
            <Grid item container xs={9} alignItems="center" gap={1}>
              <Controller
                name="taxDeductionCard.additionalWithholdingRate"
                control={control}
                rules={{
                  required: {
                    value: canSavePaymentInfoOnly()
                      ? false
                      : editTaxDeductionCard || editWithholdingRate,
                    message: "required is false",
                  },
                  validate: validatePercentage,
                }}
                render={({ field: { name, value, onChange } }) =>
                  editTaxDeductionCard || editWithholdingRate ? (
                    <NumericFormat
                      name={name}
                      value={value}
                      decimalSeparator=","
                      valueIsNumericString
                      customInput={TextField}
                      onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                      }}
                      error={
                        !!errors.taxDeductionCard?.additionalWithholdingRate
                      }
                      size="small"
                    />
                  ) : (
                    <Typography>
                      {additionalWithHoldingPercentageDisplayValue}
                    </Typography>
                  )
                }
              />
            </Grid>
            {/*insurancePaymentOutsideA1*/}
            <Grid item xs={3} />
            <Grid
              item
              container
              xs={9}
              alignItems="center"
              gap={1}
              sx={{ flexWrap: "nowrap" }}
            >
              <Controller
                name="taxInformation.insurancePaymentOutsideA1"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    <Checkbox
                      disabled={!editSalaryAndTaxInfo}
                      sx={{
                        "&.MuiCheckbox-root": {
                          paddingLeft: "0px",
                          paddingRight: "0px",
                        },
                      }}
                      checked={value}
                      onChange={(_, checked) => onChange(checked)}
                    />
                    <Typography>
                      {t("taxInformation:insurancePaymentOutsideA1")}
                    </Typography>
                  </>
                )}
              />
            </Grid>
          </FormSection>
        </UnsavedChangesProvider>
      </FormProvider>
    </Box>
  );
};

export default PaymentAndTaxInformation;
